/**
 * Firestore Service for Real-Time Events
 *
 * Handles connection to Firestore (production or emulator) for subscribing to
 * real-time agent progress events.
 */

import { type FirebaseApp, getApp, initializeApp } from 'firebase/app';
import {
  type Auth,
  connectAuthEmulator,
  getAuth,
  inMemoryPersistence,
  setPersistence,
  signInWithCustomToken,
} from 'firebase/auth';
import {
  collection,
  connectFirestoreEmulator,
  type Firestore,
  getDocs,
  getFirestore,
  limit,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  startAfter,
  type Unsubscribe,
  where,
  type WhereFilterOp,
} from 'firebase/firestore';

import type { AgentProgressEvent } from '~/components/AiChat/AgentTimeline/types';
import type { FirebaseWebAppConfig, FirestoreConnectionConfig, FirestoreQueryConfig } from '~/components/AiChat/types';

const DEFAULT_EMULATOR_HOST = 'localhost';
const DEFAULT_FIRESTORE_PORT = 9097;
const DEFAULT_AUTH_PORT = 9098;
const DEFAULT_DATABASE_ID = '(default)';
const EMULATOR_HOSTS = ['localhost', '127.0.0.1', 'host.docker.internal'] as const;

const DEFAULT_FIREBASE_APP_NAME = 'firestore-chat';
const NOTIFICATIONS_FIREBASE_APP_NAME = 'firestore-notifications';
const FNOL_TIMELINE_FIREBASE_APP_NAME = 'firestore-fnol-timeline';
const CONDUCTOR_STATUS_FIREBASE_APP_NAME = 'firestore-conductor-status';
const DOCUMENT_PROGRESS_FIREBASE_APP_NAME = 'firestore-document-progress';

/**
 * Cap on how many events the Firestore query retains, so a long agent run can't stream an unbounded history.
 * Also the size of a page of earlier history.
 */
export const DEFAULT_EVENT_LIMIT = 100;

/** Where a read of earlier history starts: after this doc. Opaque to consumers, who only hand it back. */
export type EventCursor = QueryDocumentSnapshot;

/** A read of earlier history, newest first. */
export interface EarlierEvents {
  events: AgentProgressEvent[];
  /** Where the next read starts, or `null` once the start of the history is loaded. */
  before: EventCursor | null;
}

interface EarlierEventsOptions<Doc> {
  /** The oldest doc loaded so far; the read returns only what is older. */
  before: Doc;
  /**
   * An ISO-8601 timestamp: read every doc from here up to `before` instead of one page. Firestore compares the
   * stored strings, so pass a bound with some slack rather than an exact one.
   */
  since?: string;
}

const getOrCreateApp = (appName: string, config: Record<string, string>): FirebaseApp => {
  try {
    return getApp(appName);
  } catch {
    return initializeApp(config, appName);
  }
};

const isEmulatorMode = (config: FirestoreConnectionConfig): boolean =>
  EMULATOR_HOSTS.some((host) => config.host?.includes(host));

const parseEmulatorHost = (host: string) => {
  const cleanHost = host.replace(/^https?:\/\//, '');
  const [hostname, portStr] = cleanHost.split(':');
  return {
    hostname: hostname || DEFAULT_EMULATOR_HOST,
    port: portStr ? parseInt(portStr, 10) : DEFAULT_FIRESTORE_PORT,
  };
};

const buildFirebaseConfig = (config: FirebaseWebAppConfig): Record<string, string> => ({
  apiKey: config.api_key,
  authDomain: config.auth_domain,
  projectId: config.project_id,
  messagingSenderId: config.messaging_sender_id,
  appId: config.app_id,
  ...(config.storage_bucket && { storageBucket: config.storage_bucket }),
  ...(config.database_url && { databaseURL: config.database_url }),
});

const whereConstraints = (queryConfig: FirestoreQueryConfig): QueryConstraint[] =>
  queryConfig.where_clauses?.map((clause) => where(clause.field, clause.operator as WhereFilterOp, clause.value)) ?? [];

export const buildQueryConstraints = (
  queryConfig: FirestoreQueryConfig,
  eventLimit: number = DEFAULT_EVENT_LIMIT
): QueryConstraint[] => {
  const orderByClauses = queryConfig.order_by ?? [];
  const constraints: QueryConstraint[] = [
    ...whereConstraints(queryConfig),
    ...orderByClauses.map((orderClause) => orderBy(orderClause.field, orderClause.direction as 'asc' | 'desc')),
  ];

  // Bound the result set so a long-running agent (which can emit hundreds of progress docs) can't
  // stream an unbounded history into the client. Either way we keep only the *most recent* events:
  // `limit` keeps the front of a descending result (the production config), while `limitToLast`
  // keeps the tail of an ascending result — so the live tail is never truncated down to the oldest
  // N events. With no ordering there's no well-defined "most recent", so we leave it to the client-
  // side event cap.
  const primaryOrder = orderByClauses[0];
  if (primaryOrder?.direction === 'desc') {
    constraints.push(limit(eventLimit));
  } else if (primaryOrder?.direction === 'asc') {
    constraints.push(limitToLast(eventLimit));
  }

  return constraints;
};

/**
 * Where earlier history starts: after the live window's oldest doc, while the window is full. A window short of
 * the limit already holds the whole history, and an unordered query has no limit at all, so for either there is
 * nothing before it. Only a descending window has a cursor: {@link FirestoreService.fetchEarlier} reads newest
 * first, and the composite index behind an ascending query serves only that direction.
 */
export const windowCursor = <Doc>(
  docs: readonly Doc[],
  direction: string | undefined,
  eventLimit: number = DEFAULT_EVENT_LIMIT
): Doc | null => {
  if (docs.length < eventLimit || direction !== 'desc') return null;

  return docs[docs.length - 1];
};

/**
 * Whether a snapshot's window starts the history over. The first window does, and so does a full one whose oldest
 * doc was not in the window before it: one snapshot slid it past everything the client had (a burst, or a tab that
 * was offline), leaving a gap that reads of earlier history cannot fill, since they start below what is loaded.
 * `rtu_events` are append-only, so a full window that reaches back into the one before it joins it with no gap, and
 * one short of the limit holds the whole history.
 */
export const startsHistoryOver = (
  windowEventIds: readonly string[],
  earlier: unknown,
  previousEventIds: ReadonlySet<string> | null
): boolean =>
  previousEventIds === null || (earlier !== null && !previousEventIds.has(windowEventIds[windowEventIds.length - 1]));

/**
 * Where the read after this one starts, given its docs newest first. A page short of the limit reached the start
 * of the history. A range has no limit, so it continues from the oldest doc it found, and one that found nothing
 * leaves the cursor where it was.
 */
export const nextCursor = <Doc>(docs: readonly Doc[], { before, since }: EarlierEventsOptions<Doc>): Doc | null => {
  if (since !== undefined) return docs.length ? docs[docs.length - 1] : before;

  return docs.length < DEFAULT_EVENT_LIMIT ? null : docs[docs.length - 1];
};

class FirestoreService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private firestore: Firestore | null = null;
  private unsubscribe: Unsubscribe | null = null;

  /**
   * @param appName Firebase app name. Each independent consumer (e.g. chat vs. STP
   * notifications) must use a distinct app so they get isolated connections and auth
   * identities — `signInWithCustomToken` swaps the active user on a given app, so sharing
   * one app across consumers with different tokens corrupts both.
   */
  constructor(private readonly appName: string = DEFAULT_FIREBASE_APP_NAME) {}

  get initialized(): boolean {
    return this.firestore !== null;
  }

  async initialize(
    firestoreConfig: FirestoreConnectionConfig,
    firebaseConfig: FirebaseWebAppConfig,
    customToken: string
  ): Promise<void> {
    if (this.initialized) {
      this.disconnect();
    }

    try {
      const useEmulator = isEmulatorMode(firestoreConfig);
      await (useEmulator
        ? this.initializeEmulator(firestoreConfig, customToken)
        : this.initializeProduction(firestoreConfig, firebaseConfig, customToken));
    } catch (error) {
      this.disconnect();
      throw error;
    }
  }

  subscribe(
    collectionName: string,
    queryConfig: FirestoreQueryConfig,
    /**
     * Called once per Firestore snapshot with that snapshot's newly-added docs and the ids of any docs
     * the bounded query dropped (`removedEventIds`). Delivering the batch (rather than invoking per doc)
     * lets the consumer commit a single state update per snapshot, so render work stays ~O(snapshots)
     * instead of O(events). Honouring removals keeps the consumer's state in sync with the bounded
     * query — as newer events arrive, the `limit`/`limitToLast` window evicts the oldest docs, and
     * those `removed` changes must be pruned or stale events linger client-side.
     */
    onEvents: (events: AgentProgressEvent[], isHistorical: boolean, removedEventIds: string[]) => void,
    onError?: (error: Error) => void,
    /**
     * Called right after the first (initial) snapshot's events are delivered — even when that
     * snapshot is empty. Lets consumers end a loading state on the initial data instead of flashing
     * an empty state in the gap between connecting and the historical events arriving.
     *
     * Receives where earlier history starts, for {@link FirestoreService.fetchEarlier} (`null` when the
     * window holds the whole history), and the ids of the events in the window. Called again after a
     * later snapshot whose window starts the history over (see {@link startsHistoryOver}); the events
     * outside that window no longer join up with it.
     */
    onWindow?: (earlier: EventCursor | null, windowEventIds: string[]) => void,
    /** How many of the most recent events the query keeps; defaults to {@link DEFAULT_EVENT_LIMIT}. */
    eventLimit?: number
  ): Unsubscribe {
    if (!this.firestore) {
      throw new Error('Firestore service not initialized');
    }

    const collectionRef = collection(this.firestore, collectionName);
    const constraints = buildQueryConstraints(queryConfig, eventLimit);
    const firestoreQuery = query(collectionRef, ...constraints);
    const direction = queryConfig.order_by?.[0]?.direction;

    let isInitialSnapshot = true;
    let previousEventIds: ReadonlySet<string> | null = null;

    this.unsubscribe = onSnapshot(
      firestoreQuery,
      (snapshot) => {
        const isHistorical = isInitialSnapshot;
        isInitialSnapshot = false;

        const changes = snapshot.docChanges();
        // `rtu_events` are append-only: the agent service emits a fresh doc (new `event_id`) for every
        // progress update and never mutates one in place, so a step transition arrives as a new `added`
        // doc rather than a `modified` one. We therefore only handle `added` (new events) and `removed`
        // (docs the bounded query evicted) and intentionally ignore `modified`. If that contract ever
        // changes, fold `modified` into `addedEvents` AND drop the consumer's `event_id` dedup guard so
        // the re-`set` can overwrite the existing entry.
        const addedEvents = changes
          .filter((change) => change.type === 'added')
          .map((change) => change.doc.data() as AgentProgressEvent);
        const removedEventIds = changes
          .filter((change) => change.type === 'removed')
          .map((change) => (change.doc.data() as AgentProgressEvent).event_id);

        if (addedEvents.length > 0 || removedEventIds.length > 0) {
          onEvents(addedEvents, isHistorical, removedEventIds);
        }

        const windowEventIds = snapshot.docs.map((doc) => (doc.data() as AgentProgressEvent).event_id);
        const earlier = windowCursor(snapshot.docs, direction, eventLimit);
        if (startsHistoryOver(windowEventIds, earlier, previousEventIds)) {
          onWindow?.(earlier, windowEventIds);
        }
        previousEventIds = new Set(windowEventIds);
      },
      (error) => onError?.(error)
    );

    return this.unsubscribe;
  }

  /**
   * One read of the history before `before`, with the subscription's own filters, newest first: a page of
   * {@link DEFAULT_EVENT_LIMIT} docs, or every doc from `since` on. The live query must order by `desc` (only
   * then does {@link windowCursor} hand out a cursor), so the composite index behind it serves this read; a
   * doc-snapshot cursor is exact — Firestore orders by document name after the explicit order,
   * so docs sharing the boundary timestamp are neither skipped nor read twice.
   */
  async fetchEarlier(
    collectionName: string,
    queryConfig: FirestoreQueryConfig,
    options: EarlierEventsOptions<EventCursor>
  ): Promise<EarlierEvents> {
    if (!this.firestore) {
      throw new Error('Firestore service not initialized');
    }

    const field = queryConfig.order_by?.[0]?.field;
    // An unordered subscription is not bounded, so its window already holds everything.
    if (!field) return { events: [], before: null };

    const { before, since } = options;
    const snapshot = await getDocs(
      query(
        collection(this.firestore, collectionName),
        ...whereConstraints(queryConfig),
        orderBy(field, 'desc'),
        ...(since === undefined ? [limit(DEFAULT_EVENT_LIMIT)] : [where(field, '>=', since)]),
        startAfter(before)
      )
    );

    return {
      events: snapshot.docs.map((doc) => doc.data() as AgentProgressEvent),
      before: nextCursor(snapshot.docs, options),
    };
  }

  disconnect(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.firestore = null;
    this.auth = null;
    this.app = null;
  }

  private async initializeProduction(
    firestoreConfig: FirestoreConnectionConfig,
    firebaseConfig: FirebaseWebAppConfig,
    customToken: string
  ): Promise<void> {
    this.app = getOrCreateApp(this.appName, buildFirebaseConfig(firebaseConfig));
    this.auth = getAuth(this.app);
    // In memory, not the default IndexedDB: that shares one user across browser tabs, so another claim's tab
    // would replace this tab's token and its next reconnect would be denied.
    await setPersistence(this.auth, inMemoryPersistence);
    await signInWithCustomToken(this.auth, customToken);
    this.firestore = getFirestore(this.app, firestoreConfig.database ?? DEFAULT_DATABASE_ID);
  }

  private async initializeEmulator(config: FirestoreConnectionConfig, customToken: string): Promise<void> {
    this.app = getOrCreateApp(this.appName, { apiKey: 'fake-api-key', projectId: config.project_id });

    const { hostname, port } = parseEmulatorHost(`${DEFAULT_EMULATOR_HOST}:${DEFAULT_FIRESTORE_PORT}`);

    this.firestore = getFirestore(this.app, config.database ?? DEFAULT_DATABASE_ID);
    try {
      connectFirestoreEmulator(this.firestore, hostname, port);
    } catch (error) {
      // Ignore error if already connected to emulator
      if (error instanceof Error && !error.message.includes('Cannot call')) {
        throw error;
      }
    }

    this.auth = getAuth(this.app);
    try {
      connectAuthEmulator(this.auth, `http://${hostname}:${DEFAULT_AUTH_PORT}`, { disableWarnings: true });
    } catch (error) {
      // Ignore error if already connected to emulator
      if (error instanceof Error && !error.message.includes('Cannot call')) {
        throw error;
      }
    }

    await setPersistence(this.auth, inMemoryPersistence);
    await signInWithCustomToken(this.auth, customToken);
  }
}

/** Shared singleton for AI chat real-time updates. */
export const firestoreService = new FirestoreService();

/**
 * Separate instance for STP claim notifications. Uses its own Firebase app + auth so opening
 * the Automation First tab does not disconnect or re-authenticate the chat connection.
 */
export const notificationsFirestoreService = new FirestoreService(NOTIFICATIONS_FIREBASE_APP_NAME);

/**
 * Separate instance for the FNOL progress timeline. Its subscription is torn down and rebuilt on
 * every inbox selection change, which would disconnect either of the instances above.
 */
export const fnolTimelineFirestoreService = new FirestoreService(FNOL_TIMELINE_FIREBASE_APP_NAME);

/**
 * Separate instance for the claim page's one conductor status subscription, which the claim bar's pill and
 * the Automation First tab share through `ConductorStatusProvider`: an instance holds one listener, so a
 * second subscription would end the first. It reads its topic beside the tab's conductor stream on
 * `notificationsFirestoreService`, signed in with a token for another topic; sharing that app would end one
 * subscription and sign the other in with the wrong token.
 */
export const conductorStatusFirestoreService = new FirestoreService(CONDUCTOR_STATUS_FIREBASE_APP_NAME);

/** Public shape of a Firestore service instance, for consumers that accept an injected service. */
export type FirestoreServiceInstance = typeof firestoreService;

/**
 * Separate instance for the claim Documents tab's live processing rows, so opening the tab never
 * disconnects the chat, STP or conductor-status subscriptions.
 */
export const documentProgressFirestoreService = new FirestoreService(DOCUMENT_PROGRESS_FIREBASE_APP_NAME);
